/**
 * All user-facing error copy for ednov-web.
 * Generic, internal, Firebase, and backend catalogs live here.
 * Mapping and distribution stay in utils — UI must go through {@link getErrorMessage}.
 */

// ── Generic ──────────────────────────────────────────────────────────────────

export const GenericErrors = {
  // fallback
  generic: 'Une erreur est survenue. Veuillez réessayer.',

  // network
  network: 'Erreur de connexion. Vérifiez votre connexion internet et réessayez.',

  // token expired
  sessionExpired: 'Votre session a expiré. Veuillez vous reconnecter.',

  // unavailable
  unavailable:
    'Cette fonctionnalité est temporairement indisponible. Veuillez réessayer plus tard.',

  // access
  accessDenied: "Vous n'avez pas l'autorisation d'effectuer cette action.",

  // bad request
  badRequest: 'Requête invalide. Vérifiez les informations saisies.',

  // not found
  notFound: 'Ressource introuvable.',

  // method
  methodNotAllowed: "Cette action n'est pas autorisée.",

  // timeout
  requestTimeout: 'La requête a expiré. Veuillez réessayer.',

  // conflict
  conflict: 'Conflit détecté. Cette opération ne peut pas être effectuée.',

  // payload
  payloadTooLarge: 'Données trop volumineuses. Veuillez réduire la taille.',

  // media
  unsupportedMedia: 'Format non supporté.',

  // validation
  unprocessable: 'Les données fournies ne peuvent pas être traitées.',

  // rate limit
  tooManyRequests: 'Trop de tentatives. Veuillez patienter avant de réessayer.',

  // server
  serverError: 'Erreur serveur. Veuillez réessayer dans quelques instants.',

  // gateway
  gatewayTimeout: 'Le serveur met trop de temps à répondre. Veuillez réessayer.',
} as const;

/** HTTP status → generic catalog key used by {@link messageFromApiError}. */
export const HttpStatusErrors: Readonly<Record<number, keyof typeof GenericErrors>> = {
  400: 'badRequest',
  401: 'sessionExpired',
  403: 'accessDenied',
  404: 'notFound',
  405: 'methodNotAllowed',
  408: 'requestTimeout',
  409: 'conflict',
  413: 'payloadTooLarge',
  415: 'unsupportedMedia',
  422: 'unprocessable',
  429: 'tooManyRequests',
  500: 'serverError',
  502: 'unavailable',
  503: 'unavailable',
  504: 'gatewayTimeout',
};

// ── Internal (app-owned, not backend / Firebase / generic fallbacks) ─────────
// `{param}` templates are substituted by utils.

export const InternalErrors = {
  // infra
  unstableConnection: 'Connexion instable, nouvelle tentative…',
  connectFailed: 'Impossible de se connecter.',
  serverTemporarilyUnavailable:
    'Le serveur est temporairement indisponible. Vérifiez votre connexion et réessayez.',
  invalidApiResponse: 'Réponse API invalide.',
  createdUserMissingId: 'Réponse API sans identifiant utilisateur après création.',

  // auth
  emailRequired: 'Veuillez saisir votre adresse email.',
  userNotSignedIn: 'Utilisateur non connecté.',
  accountPendingActivation:
    "Votre compte n'est pas encore activé. Utilisez le lien « Activer mon compte » reçu par email, ou demandez à un administrateur de votre organisation de vous le renvoyer.",
  tutorAppAccessDenied: "Vous n'êtes pas autorisé à accéder à cette application.",
  passwordsMismatch: 'Les mots de passe ne correspondent pas.',
  passwordCriteriaIncomplete: 'Le mot de passe ne respecte pas tous les critères.',
  invalidActivationLink: "Lien d'activation invalide.",
  invalidResetLink: 'Lien de réinitialisation invalide ou expiré.',
  authBootstrapTimeout: "Délai d'authentification dépassé, veuillez réessayer.",
  passwordMinLength: 'Le mot de passe doit contenir au moins 6 caractères.',
  otpCodeIncomplete: 'Veuillez saisir le code complet.',
  loginCodeRequired: 'Le code de connexion est obligatoire.',
  loginCodeNumeric: 'Le code de connexion doit être uniquement numérique (0–9).',
  loginCodeSixDigits: 'Le code de connexion doit faire exactement 6 chiffres.',
  contactRequired: 'Un email ou numéro de téléphone est requis.',
  contactInvalid: 'Veuillez saisir un email ou un numéro de téléphone valide.',
  passwordRulesFailed: 'Le mot de passe ne respecte pas les critères suivants : {rules}',

  // users
  noUserSelected: 'Aucun utilisateur sélectionné.',
  noPendingConfirmation: 'Aucune confirmation en attente.',
  userNotFound: 'Utilisateur introuvable.',
  emailAlreadyExists: 'Cette adresse email est déjà utilisée par un autre compte.',
  phoneAlreadyExists: 'Ce numéro de téléphone est déjà utilisé par un autre compte.',
  contactsIdentical:
    'Le contact principal et le contact secondaire ne peuvent pas être identiques.',
  clipboardCopyFailed: 'Impossible de copier le lien. Copiez-le manuellement.',
  phoneSetupLinkMissing:
    'Le numéro a été mis à jour mais le lien de configuration n’a pas pu être généré.',
  displayNameAlreadyExists: 'Ce nom complet existe déjà, ajoutez un caractère supplémentaire.',
  firstSelectManager: "Veuillez d'abord sélectionner un manager.",
  firstSelectCoordinator: "Veuillez d'abord sélectionner un coordinateur.",
  managedByRequired: 'Le champ "Géré par" est obligatoire.',
  genderRequired: 'Le genre est obligatoire.',
  nameMinTwoChars: 'Le nom doit contenir au moins 2 caractères.',
  ageRange18to70: "L'âge doit être entre 18 et 70 ans.",
  atLeastOneRole: 'Au moins un rôle doit être sélectionné.',
  residenceDepartmentRequiredForManager:
    'Le département de résidence est obligatoire pour un manager ou un coordinateur.',
  phoneShareRequired: 'Veuillez indiquer si vous acceptez de partager votre téléphone.',
  phoneRequired: 'Le téléphone est requis.',
  birthDateRequired: 'La date de naissance est obligatoire.',
  addressRequired: "L'adresse est obligatoire.",
  postalCodeRequired: 'Le code postal est obligatoire.',
  cityRequired: 'La ville est obligatoire.',
  departmentRequired: 'Le département est obligatoire.',
  countryRequired: 'Le pays est obligatoire.',
  exitDateRequired: 'La date de sortie est obligatoire.',
  effectiveExitDateRequired: 'La date de sortie effective est obligatoire.',
  archiveEligibilityFailed: "Impossible de vérifier les conditions d'archivage.",
  reactivationEligibilityFailed: 'Impossible de vérifier les conditions de réactivation.',
  reactivationNotEligible:
    'Cet utilisateur ne peut pas être réactivé (statut actuel incompatible).',
  reactivationConfirmRecalc:
    "Le statut et la situation seront recalculés selon l'avancement du dossier.",
  reactivationNotAutoActive: "L'utilisateur ne repassera pas automatiquement au statut Actif.",

  // tutors inperson
  tutorNotFound: 'Tuteur introuvable.',
  missingTutorId: 'Identifiant tuteur manquant.',
  informationMeetingParticipationDateRequired:
    "La date de participation à la réunion d'information est obligatoire.",
  informationMeetingParticipationDateInvalid:
    "La date de participation à la réunion d'information est invalide.",
  informationMeetingParticipationDateOutOfRange:
    "La date de participation à la réunion d'information doit être comprise entre le 1er janvier de l'année en cours et aujourd'hui.",
  engagementStartRequired: "La date de début d'engagement est obligatoire.",
  engagementEndRequired: "La date de fin d'engagement est obligatoire.",
  engagementEndBeforeStart:
    "La date de fin d'engagement ne peut pas être antérieure à la date de début.",
  engagementEndAfterStart: "La date de fin d'engagement doit être postérieure à la date de début.",
  studentsToSupportRequired: "Le nombre d'élèves à accompagner est obligatoire.",
  teachingSubjectRequired: 'La matière à enseigner est obligatoire.',
  weekendAvailabilityRequired: 'Veuillez indiquer votre disponibilité le week-end.',
  holidayAvailabilityRequired: 'Veuillez indiquer votre disponibilité pendant les jours fériés.',
  vacationAvailabilityRequired: 'Veuillez indiquer votre disponibilité pendant les vacances.',
  availabilitiesRequired: 'Les disponibilités sont obligatoires.',
  valuedEngagementRequired: "L'engagement associatif valorisé est obligatoire.",
  criminalRecordRequired: 'Le casier judiciaire (PDF) est obligatoire.',
  criminalRecordPdfRequiredToDecide: 'Le PDF doit être uploadé pour pouvoir valider ou refuser.',
  contractDocumentRequired: 'Le document de contrat (PDF) est obligatoire.',
  engagementDocumentRequired: "Le document d'engagement (PDF) est obligatoire.",
  professionalSituationRequired: 'La situation professionnelle est obligatoire.',
  commitment_start_date_cannot_be_in_the_past: 'La date de début ne peut pas être dans le passé.',
  commitment_end_date_too_early: 'La date de fin doit être au moins 3 mois après la date de début.',

  // tutors remote
  customEngagementMinMonths: "La durée personnalisée doit être d'au moins {minMonths} mois.",
  tutorMinAge: 'Le tuteur doit avoir au moins {minAge} ans.',
  archiveBlockedTutor:
    'Impossible d’archiver ce tuteur : il est en cours de tutorat. Veuillez dissocier ses binômes actifs avant de procéder à l’archivage.',
  tutorActivePairsBlockArchive:
    '{tutorName} accompagne actuellement des élèves. L’archiver désactiverait son compte et interromprait le suivi pédagogique en cours. Dissociez-le d’abord de ses binômes avant d’effectuer cette action.',
  tutorActivePairsBlockCancelCriminalRecord:
    '{tutorName} accompagne actuellement des élèves. Annuler la validation de son casier judiciaire lui retirerait l’habilitation requise pour le tutorat et interromprait l’accompagnement. Dissociez-le d’abord de ses binômes avant d’effectuer cette action.',

  // students remote
  archiveBlockedStudent:
    'Impossible d’archiver cet élève : il est en cours de tutorat. Veuillez dissocier son binôme avant de procéder à l’archivage.',
  studentActivePairsBlockArchive:
    '{studentName} est actuellement accompagné dans un tutorat en cours. L’archiver désactiverait son compte et interromprait le suivi pédagogique. Dissociez-le d’abord de son binôme avant d’effectuer cette action.',
  reactivationNoParent:
    'Impossible de réactiver cet élève : aucun parent n’est associé à son dossier. Veuillez lui associer un parent actif.',
  reactivationParentNotActive:
    'Impossible de réactiver cet élève : son parent doit être actif. Veuillez vérifier et finaliser le dossier du parent.',
  reactivationParentFirstHint: 'Réactivez d’abord le parent, puis réessayez de réactiver l’élève.',

  // parents
  parentNotFound: 'Parent introuvable.',
  archiveBlockedParent:
    'Impossible d’archiver ce parent : un ou plusieurs de ses enfants sont en cours de tutorat. Veuillez dissocier les binômes concernés avant de procéder à l’archivage.',
  archiveCascadeChildren: '{count} enfant(s) seront également archivé(s).',

  // volunteers
  volunteerNotFound: 'Volontaire introuvable.',

  // admins
  adminNotFound: 'Collaborateur introuvable.',

  // students inperson
  studentNotFound: 'Élève introuvable.',
  studentRoleNotFound: 'Rôle élève introuvable.',
  csvRequired: 'Veuillez sélectionner un fichier (.xlsx ou .csv).',
  studentImportNone: 'Aucun élève importé.',
  studentImportInvalidRows:
    '{invalidCount} ligne(s) invalide(s). Le détail est affiché dans la fenêtre d’import.',
  academicLevelRequired: 'Le niveau académique est obligatoire.',
  schoolSituationRequired: 'La situation scolaire est obligatoire.',
  schoolEstablishmentRequired: "L'établissement scolaire est obligatoire.",

  // history
  loadHistoryFailed: "Erreur lors du chargement de l'historique.",

  // sessions inperson
  requiredAttendanceReasons: "Veuillez compléter les présences (motifs d'absence requis).",
  requiredRecurrenceDates: 'Veuillez configurer la plage de dates pour cette récurrence.',
  noSessionSelected: 'Aucune séance sélectionnée.',
  specifyReason: 'Veuillez préciser le motif.',
  absenceReasonRequired: "Un motif d'absence est requis.",
  cancelReasonRequired: "Veuillez sélectionner un motif d'annulation.",
  cancelCommentRequired: 'Un commentaire est requis pour le motif "Autre".',
  loadParticipantsFailed: 'Impossible de charger les participants.',
  loadSessionDataFailed: 'Impossible de charger les données de la séance.',
  sessionNotFound: 'Séance introuvable.',
  cannotTrackBulkCreate:
    'Impossible de suivre la création. Consultez la liste des séances pour vérifier où elle en est.',
  createMaybeStarted:
    'Vérifiez la liste des séances avant de réessayer : la création a peut-être déjà démarré.',
  autoAssignNeedTutor: "Ajoutez au moins un tuteur avant l'affectation automatique.",
  autoAssignNeedStudent: 'Aucun élève à répartir.',
  autoAssignNeedEstablishment: "Sélectionnez un établissement avant l'affectation automatique.",
  autoAssignNeedSessionType: "Sélectionnez d'abord un type de séance.",
  dateTimeInvalid: 'Date et heure invalides.',
  deadlineOutsideHours: "L'horaire doit être compris entre 8 h et 18 h.",
  tutorAvailabilityChecking: 'Vérification de la disponibilité des tuteurs…',
  tutorSlotConflictOne:
    "1 tuteur n'est pas disponible à cet horaire. La séance ne pourra pas être enregistrée tant que le conflit persiste.",
  studentAvailabilityChecking: 'Vérification de la disponibilité des élèves…',
  studentSlotConflictOne:
    "1 élève n'est pas disponible à cet horaire. La séance ne pourra pas être enregistrée tant que le conflit persiste.",
  sessionDateRequired: 'La date est obligatoire.',
  startTimeRequired: "L'heure de début est obligatoire.",
  sessionNotLoaded: 'Session non chargée.',
  sessionTerminalNoEdit: 'Cette séance est clôturée et ne peut plus être modifiée.',
  capacityExceeded: 'Capacité dépassée.',
  classroomCapacityExceeded: 'Capacité de la salle dépassée.',
  individualTutoringSingleTutor: 'Le tutorat individuel n’accepte qu’un seul tuteur.',
  tutorCapacityReached: 'Capacité maximale de tuteurs atteinte.',
  minFacilitatorRequired: 'Ajoutez au moins un tuteur.',
  orphanStudentsBlockSubmit:
    'La séance ne pourra pas être créée tant que des élèves restent orphelins.',
  orphanStudentsMustAssign: 'Assignez tous les élèves orphelins avant d’enregistrer.',
  participantAlreadyAddedTutor: 'Ce tuteur est déjà ajouté.',
  participantAlreadyAddedStudent: 'Cet élève est déjà ajouté.',
  studentCapacityReached: 'Capacité maximale d’élèves atteinte.',
  individualPlanificationSingleTutor: 'La planification individuelle n’accepte qu’un seul tuteur.',
  participantMissingIdTutor: 'Impossible d’ajouter le tuteur : identifiant manquant.',
  participantMissingIdStudent: 'Impossible d’ajouter l’élève : identifiant manquant.',
  autoAssignPartialWarning: 'Suggestion appliquée : certains élèves restent sans tuteur.',
  slotConflictGeneric: 'Déjà en séance sur ce créneau horaire.',
  sessionModeRequired: 'Le mode de séance est requis.',
  recurrenceTypeRequiredForRecurring:
    'Veuillez sélectionner un type de récurrence pour une séance récurrente.',
  recurrenceTypeRequired: 'Le type de récurrence est obligatoire pour une séance récurrente.',
  weekDaysRequired: 'Veuillez sélectionner au moins un jour de la semaine.',
  sessionTypeRequired: 'Type de séance obligatoire.',
  tutorRequired: 'Le tuteur est obligatoire.',
  studentRequired: "L'élève est obligatoire.",
  recurrenceRequired: 'La récurrence est obligatoire.',
  endTimeRequired: "L'heure de fin est obligatoire.",
  programRequired: 'Le programme est obligatoire.',
  atLeastOneFacilitator: 'Au moins un animateur est requis.',
  facilitatorRequired: 'Un animateur est requis.',
  sessionRequiredTitle: 'Séance requise.',
  unavailableAtSlot: 'Indisponible à cet horaire.',
  selectDateTimeBeforeParticipants:
    "Veuillez d'abord choisir la date et les horaires de la séance.",
  cannotEdit: 'Modification impossible.',
  cannotCancel: 'Annulation impossible.',
  sessionEnded: 'Séance terminée.',
  sessionCancelled: 'Séance annulée.',
  sessionClosed: 'Séance clôturée.',
  sessionNotStarted: 'Séance non démarrée.',
  sessionInProgress: 'Séance en cours.',
  sessionNotHeld: 'Séance non réalisée.',
  pairAbsent: 'Binôme absent.',
  tutorAbsent: 'Tuteur absent.',
  studentAbsent: 'Élève absent.',
  noActiveSession: 'Aucune séance active.',
  attendanceUnavailable: 'Gestion des présences indisponible.',
  groupAlreadyCancelled: 'Groupe déjà annulé.',
  sessionAlreadyCancelled: 'Séance déjà annulée.',
  editDeadlinePassed: 'Modification impossible car la date de saisie est dépassée.',
  cannotCancelEndedSessions: 'Annulation impossible : séances terminées.',
  cannotCancelEndedSession: 'Annulation impossible : séance terminée.',
  cannotEditSessionEnded: 'Modification impossible : séance terminée.',
  cannotEditSessionNotHeld: 'Modification impossible : séance non réalisée.',
  cannotEditSessionCancelled: 'Modification impossible : séance annulée.',
  cannotEditSessionClosed: 'Modification impossible : séance clôturée.',
  cannotEditPairAbsent: 'Modification impossible : binôme absent.',
  cannotEditTutorAbsent: 'Modification impossible : tuteur absent.',
  cannotEditStudentAbsent: 'Modification impossible : élève absent.',
  cannotEditClosedOrCancelled: 'Modification impossible : séance clôturée ou annulée.',
  cannotCancelAlreadyCancelled: 'Annulation impossible : séance déjà annulée.',
  cannotCancelSessionClosed: 'Annulation impossible : séance clôturée.',
  cannotCancelSessionEnded: 'Annulation impossible : séance terminée.',
  cannotCancelSessionNotHeld: 'Annulation impossible : séance non réalisée.',
  cannotCancelPairAbsent: 'Annulation impossible : binôme absent.',
  cannotCancelTutorAbsent: 'Annulation impossible : tuteur absent.',
  cannotCancelStudentAbsent: 'Annulation impossible : élève absent.',
  noGroupToUpdate: 'Aucun groupe à mettre à jour.',
  tutorUnavailableAtSlot:
    "Impossible d'enregistrer : {names} n'est pas disponible à cet horaire. Modifiez le créneau ou retirez ce tuteur.",
  tutorsUnavailableAtSlot:
    "Impossible d'enregistrer : les tuteurs suivants ne sont pas disponibles à cet horaire : {names}. Modifiez le créneau ou retirez-les.",
  studentUnavailableAtSlot:
    "Impossible d'enregistrer : {names} n'est pas disponible à cet horaire. Modifiez le créneau ou retirez cet élève.",
  studentsUnavailableAtSlot:
    "Impossible d'enregistrer : les élèves suivants ne sont pas disponibles à cet horaire : {names}. Modifiez le créneau ou retirez-les.",
  alreadyBusyAtSlot: '{name} est déjà occupé(e) à cet horaire.',
  bulkCreateStillRunning:
    'Création toujours en cours ({createdSessions} séance(s) créée(s) à ce stade). Consultez la liste des séances dans quelques minutes.',
  studentsTrimmedAfterTypeChange:
    'Type de séance modifié : {removedCount} élève(s) retiré(s) (capacité max: {newMax})',
  bulkCreatePartialFailure: '{created} séance(s) créée(s), {failed} en échec. {detail}',
  tutorSlotConflictMany:
    '{count} tuteurs ne sont pas disponibles à cet horaire. La séance ne pourra pas être enregistrée tant que les conflits persistent.',
  studentSlotConflictMany:
    '{count} élèves ne sont pas disponibles à cet horaire. La séance ne pourra pas être enregistrée tant que les conflits persistent.',
  maxTutorsCapacityReached: 'Capacité maximale tuteurs atteinte ({max})',
  maxStudentsCapacityReached: 'Capacité maximale élèves atteinte ({max})',
  removeStudentsOverCapacity: 'Vous devez retirer {count} élève(s) (maximum {max}).',
  removeTutorsOverCapacity: 'Vous devez retirer {count} tuteur(s) (maximum {max}).',
  slotConflictRange: 'Déjà en séance de {start} à {end} ce jour-là.',
  slotConflictFrom: 'Déjà en séance à partir de {start} ce jour-là.',
  studentCapacityReachedWithMax: 'Capacité maximale atteinte ({maxStudents} élève(s))',
  participantAlreadyNamedTutor: '{name} est déjà ajouté(e) comme tuteur.',
  participantAlreadyNamedStudent: '{name} est déjà ajouté(e) comme élève.',
  autoAssignPartialUnassigned: 'Suggestion appliquée : {unassigned} élève(s) restent sans tuteur.',
  checkingAvailabilityOf: 'Vérification de la disponibilité de {name}…',
  autoAssignUnassignedCount: '{count} élève(s) sans tuteur dans cette suggestion',

  // sessions remote
  joinMeeting: 'Impossible de rejoindre la réunion. Veuillez réessayer.',
  visioLinkRequired: 'Le lien visio est obligatoire pour une séance à distance.',
  visioLinkInvalid: 'Le lien doit être une URL valide (http:// ou https://).',
  joinMeetingFailed: "L'inscription à la réunion a échoué. Veuillez réessayer.",
  cancelMeetingJoinFailed: "L'annulation de l'inscription a échoué. Veuillez réessayer.",
  recurrenceIntervalInvalid: "L'intervalle de récurrence doit être supérieur ou égal à 1.",
  recurrenceDateInvalid: 'Une ou plusieurs dates de récurrence sont invalides.',
  recurrenceScopeStatusConflict:
    'Impossible de modifier le statut lors de la mise à jour de cette séance et des suivantes.',
  loadMeetingsFailed: 'Impossible de charger les réunions disponibles.',
  visioPopupBlockedTitle: "La visioconférence n'a pas pu s'ouvrir.",
  visioPopupBlockedDetail:
    "Safari semble avoir bloqué l'ouverture de la visioconférence. Autorisez les fenêtres surgissantes pour ce site, puis réessayez.",
  sessionTerminalNoEditWithStatus:
    'Modification impossible. Cette séance a un statut terminal ({status}).',

  // dashboard
  loadStatsFailed:
    'Impossible de charger les statistiques. Vérifiez votre connexion puis réessayez.',
  statsUnavailable:
    'Les statistiques sont indisponibles pour le moment. Réessayez dans quelques instants.',
  loadChartFailed: 'Impossible de charger les données.',
  loadDashboardFailed: 'Erreur de chargement du dashboard.',
  loadDashboardFailedDetail: 'Une erreur est survenue lors du chargement du dashboard.',
  loadDashboardSectionFailed: 'Erreur de chargement de cette section.',

  // help chat
  helpChatUnavailable:
    "L'assistant n'est pas disponible pour le moment. Réessayez plus tard ou contactez le support.",

  // reports inperson
  reportNotFound: 'Compte rendu introuvable.',
  requiredReportFields: 'Veuillez compléter les champs obligatoires du compte rendu.',
  loadReportFailed: 'Erreur lors du chargement du compte rendu.',
  loadReportsFailed: 'Impossible de charger les comptes rendus.',
  reportSynthesisRequired: 'La synthèse libre est obligatoire.',
  engagementLevelRequired: "Le niveau d'engagement est obligatoire.",
  reportValidatedReadOnly: 'Ce compte rendu a été validé et ne peut plus être modifié.',
  reportNoWritePermission: "Vous n'avez pas la permission de modifier ce compte rendu.",
  noReportForSession: "Aucun compte rendu n'existe pour cette séance.",
  unknownReportType: 'Type de compte rendu non reconnu.',
  sessionRequiredToCreateReport: 'Sélectionnez une séance pour créer un compte rendu.',
  reportReadOnly: 'Ce compte rendu est en lecture seule.',
  reportUnavailable: 'Compte rendu non disponible.',
  reportUnavailableShort: 'Compte rendu indisponible.',
  reportUnavailablePairAbsent: 'Compte rendu non disponible : binôme absent.',
  reportUnavailableNotStarted: 'Compte rendu non disponible : séance non démarrée.',
  reportUnavailableInProgress: 'Compte rendu non disponible : séance en cours.',
  reportUnavailableCancelled: 'Compte rendu non disponible : séance annulée.',
  validatedReportLocked: 'Le compte rendu validé ne peut plus être modifié.',
  reportMustBeSubmittedToValidate: 'Le compte rendu doit être soumis pour être validé.',
  selectSubmittedReportsToValidate: 'Sélectionnez des comptes rendus soumis à valider.',
  selectReportsToDelete: 'Sélectionnez des comptes rendus à supprimer.',
  waitForTutorsBeforeValidate:
    "Il est recommandé d'attendre que chaque tuteur ait renseigné le compte rendu avant de le valider.",
  confirmValidateReportAnyway: 'Voulez-vous valider ce compte rendu malgré tout ?',
  confirmValidateSelectionAnyway: 'Voulez-vous valider la sélection malgré tout ?',
  reportIncompleteTutorOne: "1 tuteur n'a pas encore renseigné sa partie du compte rendu.",
  otherReportAlsoValidated: '1 autre compte rendu de la sélection sera également validé.',
  reportIncompleteTutorsMany:
    "{count} tuteurs n'ont pas encore renseigné leur partie du compte rendu.",
  otherReportsAlsoValidatedMany:
    '{otherCount} autres comptes rendus de la sélection seront également validés.',
  bulkIncompleteReportsMany:
    "{count} comptes rendus d'accompagnement en classe ont des tuteurs n'ayant pas encore renseigné leur partie du compte rendu.",

  // reports remote
  remoteReportManualCreateUnavailable:
    'Création manuelle de comptes rendus pas encore disponible. Les CR sont créés automatiquement par le système.',
  unknownScoringType: 'Type de scoring non reconnu.',

  // vsc reports
  vscReportNotFound: 'Rapport introuvable.',
  vscReportAlreadyExists: 'Un rapport VSC existe déjà pour cette sélection.',
  vscReportsAlreadyExist: 'Des rapports VSC existent déjà pour cette sélection.',
  validatedVscLocked: 'Le rapport validé ne peut plus être modifié.',
  vscMustBeSubmittedToValidate: 'Le rapport doit être soumis pour être validé.',
  selectSubmittedVscToValidate: 'Sélectionnez des rapports VSC soumis à valider.',
  selectVscToDelete: 'Sélectionnez des rapports VSC à supprimer.',

  // establishments
  establishmentNotFound: 'Établissement introuvable.',
  requiredEstablishmentName: "Veuillez saisir le nom de l'établissement.",
  atLeastOneEstablishment: 'Au moins un établissement doit être sélectionné.',
  establishmentClassDuplicateShort: 'Doublon : même nom pour cette année scolaire.',
  establishmentClassNameRequired: 'Classe #{index} : le nom est obligatoire.',
  establishmentClassLevelRequired: 'Classe #{index} ("{name}") : le niveau est obligatoire.',
  establishmentClassSchoolYearRequired:
    'Classe #{index} ("{name}") : l\'année scolaire est obligatoire.',
  establishmentClassDuplicate:
    'Doublon : la classe "{name}" existe déjà pour cette année scolaire (lignes {lineA} et {lineB}).',

  // partners
  partnerNotFound: 'Partenaire introuvable.',
  requiredPartnerName: 'Le nom du partenaire est obligatoire.',

  // sport clubs
  sportClubNotFound: 'Club sportif introuvable.',
  requiredClubName: 'Le nom du club est obligatoire.',

  // companies
  companyContextUnavailable: 'Contexte entreprise indisponible. Veuillez recharger la page.',

  // school years
  schoolYearNotFound: 'Année scolaire introuvable.',
  schoolYearCodeFormat: 'Le code doit être au format YYYY-YYYY (ex. 2025-2026).',
  schoolYearCodeEndAfterStart: 'La deuxième année du code doit être supérieure à la première.',
  schoolYearCodeTaken: 'Le code {yearCode} existe déjà.',
  schoolYearDatesOverlap: 'Les dates chevauchent {label} ({start} – {end}).',

  // levels
  levelNotFound: 'Niveau introuvable.',
  levelRequired: 'Le niveau est obligatoire.',

  // subjects
  subjectNotFound: 'Matière introuvable.',
  atLeastOneSubject: 'Au moins une matière doit être sélectionnée.',
  subjectRequired: 'La matière est obligatoire.',

  // programs
  programNotFound: 'Programme introuvable.',
  atLeastOneProgram: 'Au moins un programme doit être sélectionné.',

  // specialties
  specialtyNotFound: 'Spécialité introuvable.',

  // holidays
  holidayNotFound: 'Vacances introuvables.',

  // roles
  roleNotFound: 'Rôle introuvable.',

  // visio backgrounds
  visioBackgroundNotFound: 'Arrière-plan visio introuvable.',

  // assessments
  assessmentNotFound: 'Évaluation introuvable.',
  selectAssessmentType: 'Sélectionnez un type de bilan.',
  cannotCompleteAssessment: "Vous n'êtes pas autorisé à compléter un bilan.",
  assessmentWrittenByCoordinator: 'Ce bilan est rédigé par le coordinateur désigné.',
  validatedAssessmentLocked: 'Le bilan validé ne peut plus être modifié.',
  cannotValidateAssessment: "Vous n'êtes pas autorisé à valider un bilan.",
  assessmentValidateRoleRestricted:
    'Seul un manager ou un administrateur peut valider ce type de bilan.',
  assessmentAlreadyValidated: 'Ce bilan est déjà validé.',
  assessmentMustBeSubmittedToValidate: 'Le bilan doit être soumis pour être validé.',
  cannotExportAssessment: "Vous n'êtes pas autorisé à exporter un bilan.",
  assessmentExportAfterValidation:
    'Le téléchargement sera disponible après validation — utilisez la consultation pour un aperçu.',
  cannotDeleteAssessment: "Vous n'êtes pas autorisé à supprimer un bilan.",

  // pairs inperson
  pairNotFound: 'Binôme introuvable.',
  cannotUnlinkArchivedPair: 'Impossible de dissocier un binôme archivé.',
  cannotEditArchivedPair: 'Modification impossible : binôme archivé.',
  pairDeleteNotImplemented: "La suppression de binôme n'est pas encore disponible.",

  // matching inperson
  assignStudentToGroup: "Glissez l'élève sous un tuteur pour l'assigner à un groupe.",
  participantLimitReached: 'Limite de participants atteinte.',

  // matching remote
  selectAvailabilitySlot: "Sélectionnez d'abord un créneau de disponibilité.",
  selectDateSlotBeforeTutor:
    "Veuillez sélectionner une date et un créneau horaire avant d'ajouter un tuteur.",
  selectDateSlotBeforeStudent:
    "Veuillez sélectionner une date et un créneau horaire avant d'ajouter un élève.",
  tutorAvailabilityCheckFailed:
    'Impossible de vérifier la disponibilité du tuteur. Veuillez réessayer.',
  studentAvailabilityCheckFailed:
    "Impossible de vérifier la disponibilité de l'élève. Veuillez réessayer.",

  // events
  loadEventFailed: "Erreur lors du chargement de l'événement.",
  eventNotFound: 'Événement introuvable.',
  atLeastOneActivity: 'Au moins une activité doit être sélectionnée.',
  eventModeRequired: "Le mode d'événement est requis.",
  eventRecurrenceTypeRequired:
    'Veuillez sélectionner un type de récurrence pour un événement récurrent.',
  eventVisioLinkRequired: 'Le lien visio est obligatoire pour un événement à distance.',
  eventTypeRequired: "Le type d'événement est obligatoire.",
  eventRecurrenceTypeMandatory:
    'Le type de récurrence est obligatoire pour un événement récurrent.',
  eventAttendanceUnavailable: 'Présences indisponibles.',
  eventParticipantAlreadyAdded: '{name} est déjà ajouté(e).',

  // event summaries
  eventSummaryNotFound: 'Synthèse introuvable.',
  loadEventSummaryFailed:
    "Impossible de charger cette synthèse. Vérifiez votre connexion ou vos droits d'accès.",
  eventSummaryNotReady:
    "La synthèse n'est pas encore disponible : l'événement n'a pas encore débuté.",

  // post its
  loadPostItsFailed: 'Impossible de charger les post-its.',

  // gamification
  loadVideoFailed: 'Impossible de charger cette vidéo.',
  loadProgressFailed: 'Impossible de charger la progression.',
  progressUnavailable: 'Progression non disponible (droits insuffisants ou profil non éligible).',
  loadLeaderboardFailed: 'Impossible de charger le classement.',
  videoInaccessible: 'La vidéo est inaccessible. Vérifiez votre connexion, puis réessayez.',

  // activities
  activityNotFound: 'Activité introuvable.',
  requiredActivityFields: "Veuillez remplir tous les champs obligatoires de l'activité.",
  selectActivityType: "Veuillez sélectionner un type d'activité ou en créer un nouveau.",

  // activity types
  activityTypeNotFound: "Type d'activité introuvable.",
  requiredActivityType: "Veuillez remplir tous les champs obligatoires du type d'activité.",

  // enterprises
  requiredEnterpriseName: "Veuillez saisir le nom de l'entreprise.",

  // geography
  geographySearchFailed: "La recherche d'adresse a échoué. Veuillez réessayer.",

  // files
  uploadFailed: "L'envoi du fichier a échoué. Veuillez réessayer.",
  downloadFailed: 'Le téléchargement du fichier a échoué. Veuillez réessayer.',
  uploadEndpointNotFound: 'Point de dépôt introuvable.',
  invalidFile: 'Ce fichier ne semble pas valide.',
  invalidFileType: "Ce fichier ne semble pas valide. Assurez-vous d'envoyer un vrai {fileKind}.",
  fileTooLarge: 'Fichier trop volumineux (max : {maxMb} Mo)',
  fileFormatRejected: 'Format non accepté — {hint}',

  // shared
  requiredFields: 'Veuillez remplir tous les champs obligatoires.',
  invalidEmail: 'Adresse email invalide.',
  exportEmpty: 'Aucun élément à exporter avec les filtres actuels.',
  exportFailed: "L'export a échoué. Veuillez réessayer.",
  deleteFailed: 'La suppression a échoué. Veuillez réessayer.',
  mobileRedirect: "Redirection impossible : l'application mobile n'est pas configurée.",
  loadFailed: 'Le chargement a échoué. Veuillez réessayer.',
  pageNotFound: 'Page introuvable.',
  fieldRequired: 'Ce champ est obligatoire.',
  atLeastOneSelectionRequired: 'Au moins une sélection est requise.',
  invalidPhone: 'Numéro de téléphone invalide.',
  invalidPhoneCountry: 'Format de numéro invalide pour ce pays.',
  invalidUrl: 'URL invalide.',
  invalidFormat: 'Format invalide.',
  accessDeniedTitle: 'Accès refusé.',
  accessDeniedPageDetail:
    "Vous ne disposez pas des permissions nécessaires pour accéder à cette page. Contactez votre administrateur si vous pensez qu'il s'agit d'une erreur.",
  requiredLabel: 'Le libellé est obligatoire.',
  requiredCode: 'Le code est obligatoire.',
  pageNotFoundDetail: "La page que vous recherchez n'existe pas ou a été déplacée.",
  errorBoundaryDetail: "Nous sommes désolés, quelque chose s'est mal passé. Veuillez réessayer.",
  errorTitle: 'Erreur.',
  phoneTooShort: 'Numéro de téléphone trop court.',
  maxAgeAfterMinAge: "L'âge maximum doit être supérieur à l'âge minimum.",
  endDateOnOrAfterStart: 'La date de fin doit être après ou égale à la date de début.',
  endDateAfterStart: 'La date de fin doit être après la date de début.',
  endTimeAfterStart: "L'heure de fin doit être après l'heure de début.",
  startDateRequired: 'La date de début est obligatoire.',
  endDateSameOrAfterStart: 'La date de fin doit être identique ou postérieure à la date de début.',
  actionUnavailable: 'Indisponible.',
  minLength: 'Minimum {minLength} caractères requis.',
  maxLength: 'Maximum {maxLength} caractères autorisés.',
  phoneMinDigits: 'Le numéro doit contenir au moins {minLength} chiffres.',
  phoneMaxDigits: 'Le numéro ne peut pas dépasser {maxLength} chiffres.',
  minValue: 'Minimum {min}.',
  maxValue: 'Maximum {max}.',
  pageOutOfRange: 'La page doit être comprise entre {min} et {max}.',

  // calendar
  calendarOauthDenied: 'La connexion à l’agenda a été annulée ou a échoué.',
  calendarTokenRevoked: 'La connexion à l’agenda a été révoquée. Reconnectez-vous.',
  calendarSyncEmpty: 'Aucun élément à synchroniser avec les filtres actuels.',
  calendarSyncFailed: 'La synchronisation du calendrier a échoué. Veuillez réessayer.',
  calendarNotConfigured: 'La synchro calendrier n’est pas configurée sur cet environnement.',
  calendarReconnectRequired: 'Reconnectez votre agenda pour continuer la synchro.',
  calendarProviderRequired: 'Choisissez Google Agenda ou Outlook avant de synchroniser.',
  calendarTypesRequired: 'Choisissez au moins un type à synchroniser.',
  calendarOauthConnected: 'Agenda connecté. Choisissez ce que vous voulez synchroniser.',
  calendarSyncDone: 'Calendrier synchronisé.',
  calendarDisconnected: 'Agenda déconnecté.',
  calendarDisconnectedKeep:
    'Les événements déjà exportés restent dans votre agenda. Les mises à jour sont arrêtées.',
  calendarSyncPaused:
    'Synchronisation en pause. Les événements déjà exportés restent dans votre agenda.',
  calendarSyncSlow: 'Le fournisseur calendrier est lent. La synchronisation continue…',
  calendarOtherProviderConnected:
    'Un autre agenda est déjà connecté. Déconnectez-le avant d’en choisir un autre.',
  calendarProviderEmailMismatch:
    'Ce fournisseur ne correspond pas au domaine de votre adresse email.',
} as const;

// ── Firebase ─────────────────────────────────────────────────────────────────

export const FirebaseErrors = {
  // login
  firebaseWrongPassword: 'Email ou mot de passe incorrect.',
  firebaseEmailInUse: 'Cette adresse email est déjà utilisée.',
  firebaseCredentialInUse: 'Ces identifiants sont déjà utilisés.',
  firebaseUserNotFound:
    "Utilisateur introuvable, êtes-vous sûr qu'un compte avec cet email existe ?",

  // phone
  firebaseInvalidOtp: 'Code OTP incorrect.',
  firebaseInvalidVerificationSession: 'Session de vérification invalide.',
  firebaseOtpExpired: 'Le code OTP a expiré.',
  firebasePhoneRequired: 'Veuillez saisir un numéro de téléphone.',
  firebaseSmsQuotaExceeded: 'Quota de SMS dépassé, réessayez plus tard.',

  // reset link
  firebaseResetLinkExpired: 'Le lien de réinitialisation a expiré.',
  firebaseResetLinkInvalid: 'Le lien de réinitialisation est invalide.',

  // limits
  firebaseTooManyRequests: 'Trop de tentatives, veuillez réessayer plus tard.',
  firebaseTimeout: 'La requête a expiré, veuillez réessayer.',

  // popups
  firebasePopupCancelled: 'Requête popup annulée.',
  firebasePopupBlocked: 'Popup bloquée par le navigateur.',
  firebasePopupClosed: "Popup fermée par l'utilisateur.",

  // setup
  firebaseInvalidConfig: 'Configuration Firebase invalide.',
  firebaseInit: "Erreur d'initialisation.",
  firebaseAuthDomainRequired: 'Configuration du domaine requise.',
  firebaseUnauthorizedDomain: 'Domaine non autorisé.',
  firebaseInvalidPersistence: 'Type de persistance invalide.',
  firebaseUnsupportedPersistence: 'Type de persistance non supporté.',
  firebaseWebStorageUnsupported: 'Stockage web non supporté par votre navigateur.',
  firebaseKeychain: "Erreur d'accès au trousseau.",
  firebaseCaptchaFailed: 'Vérification reCAPTCHA échouée.',
  firebaseInvalidArguments: 'Paramètres invalides.',

  // application
  firebaseAppDeleted: "L'application a été supprimée.",
  firebaseAppNotAuthorized: 'Application non autorisée.',
  firebaseOperationNotAllowed: "Cette opération n'est pas autorisée.",
  firebaseInternal: 'Une erreur interne est survenue.',
} as const;

/** Lookup for generic + internal + Firebase keys passed to {@link getErrorMessage}. */
export const CatalogErrors = {
  ...GenericErrors,
  ...InternalErrors,
  ...FirebaseErrors,
} as const;

/** Union of keys accepted by {@link getErrorMessage}. */
export type CatalogErrorCode = keyof typeof CatalogErrors;

// ── Backend (exact Nest messages → French UI copy) ───────────────────────────

export const BackendErrors: Readonly<Record<string, string>> = {
  // infra
  'Drain endpoint is not configured': GenericErrors.generic,
  'Invalid drain token': GenericErrors.generic,
  'Instance is draining — not ready for traffic': GenericErrors.unavailable,
  'Internal server error': GenericErrors.generic,
  'Unexpected authentication error': GenericErrors.generic,
  'Caller context is required': GenericErrors.generic,
  'Guest meeting tokens are not configured (set MEETING_ACCESS_JWT_SECRET or LIVEKIT_API_SECRET).':
    GenericErrors.unavailable,
  'Failed to provision LiveKit room': GenericErrors.unavailable,
  'LiveKit is not configured': GenericErrors.unavailable,
  'Invalid LiveKit webhook signature or payload': GenericErrors.generic,
  'LiveKit presence simulation is not available in production': GenericErrors.unavailable,
  'Set LIVEKIT_DEV_SIMULATE_WEBHOOKS=true in .env to simulate presence events locally':
    GenericErrors.generic,
  'GROQ_API_KEY is not configured': GenericErrors.unavailable,
  'AI generation is not configured (GROQ_API_KEY)': GenericErrors.unavailable,
  'Failed to save session report. The database may be missing the "script" column. Run migrations: npm run migration:run':
    GenericErrors.generic,

  // auth
  'No token provided': GenericErrors.sessionExpired,
  'Invalid token': GenericErrors.sessionExpired,
  'User is deactivated': 'Ce compte a été désactivé.',
  "User status is 'deactivated'": 'Ce compte a été désactivé.',
  'User not found for this account. The Firebase account exists but there is no matching user in the database (or the user may be deactivated).':
    "Aucun compte n'est associé à cet identifiant.",

  // users
  'country must be shorter than or equal to 2 characters':
    'Le pays doit être un code à 2 lettres (ex. FR).',
  'User not found': 'Cet utilisateur est introuvable.',
  'User id and company id are required': 'Une information obligatoire est manquante.',
  'One or more role IDs do not exist or do not belong to this company':
    'Un ou plusieurs rôles sont invalides.',
  'At least one establishment is required': 'Veuillez sélectionner au moins un établissement.',
  'managedById cannot reference the same user':
    'Un utilisateur ne peut pas être son propre responsable.',
  'managedById does not refer to a user in this company':
    'Le responsable indiqué est introuvable dans cette organisation.',
  'managedById must reference a user with an admin-level role':
    'Le responsable doit avoir un rôle administrateur.',
  'exitDate is required when status is deactivated':
    'Veuillez renseigner une date de sortie pour désactiver le compte.',
  'Invalid exitDate format (expected YYYY-MM-DD)': "La date de sortie n'est pas valide.",
  'Invalid exitDate': "La date de sortie n'est pas valide.",
  'Invalid status for staff account (active, deactivated).':
    "Ce statut n'est pas disponible pour un compte personnel.",
  'Not allowed to regenerate this invitation':
    "Vous n'êtes pas autorisé à renvoyer cette invitation.",
  'Not allowed to reactivate this user': "Vous n'êtes pas autorisé à réactiver cet utilisateur.",
  'Not allowed to reactivate users': "Vous n'êtes pas autorisé à réactiver cet utilisateur.",
  'Not allowed to archive users': "Vous n'êtes pas autorisé à archiver cet utilisateur.",
  'User is already archived': 'Cet utilisateur est déjà archivé.',
  'User is not archived or suspended':
    'Cet utilisateur ne peut pas être réactivé (statut actuel incompatible).',
  'Archiving must go through the archive endpoint':
    "L'archivage doit passer par le parcours d'archivage.",
  'Onboarding record not found for $1 user $2': "Le dossier d'inscription est introuvable.",
  "Ce compte n'est pas encore activé.": "Ce compte n'est pas encore activé.",
  'Not allowed to reset this password': "Vous n'êtes pas autorisé à réinitialiser ce mot de passe.",
  'Password reset email was NOT sent':
    "L'email de réinitialisation n'a pas pu être envoyé. Réessayez.",
  "Cet utilisateur n'a pas d'adresse email (compte téléphone uniquement).":
    "Cet utilisateur n'a pas d'adresse email.",
  'You can only update your own profile': 'Vous ne pouvez modifier que votre propre profil.',
  'Cannot send an activation email to a user without an email':
    "Impossible d'envoyer l'e-mail d'activation : aucune adresse e-mail n'est renseignée.",
  'Cannot send an activation email to a user without a firebase_uid':
    "Impossible d'envoyer l'e-mail d'activation : le compte n'est pas encore lié.",
  'Email is required when inviteBy is "email"': 'Veuillez renseigner une adresse e-mail.',
  'Phone is required when inviteBy is "phone"': 'Veuillez renseigner un numéro de téléphone.',
  'primaryContactKind must be one of the following values: email, phone':
    'Le contact principal doit être un e-mail ou un téléphone.',
  'Invitations cannot be sent to users with the students role group':
    'Les invitations ne peuvent pas être envoyées aux élèves.',
  'Query param "credential" is required (phone or email).':
    'Veuillez indiquer un e-mail ou un numéro de téléphone.',
  'Access denied: onboarding record outside your visibility scope':
    "Ce dossier n'est pas dans votre périmètre.",
  'No sections provided': 'Aucune information à enregistrer.',
  'Cannot replace a validated criminal record':
    'Impossible de remplacer un casier judiciaire déjà validé.',
  'Failed to save criminal record': "L'enregistrement du casier judiciaire a échoué.",
  'No criminal record on file': 'Aucun casier judiciaire enregistré.',
  'No criminal record to act on': 'Aucun casier judiciaire à traiter.',
  'Invalid action': "Cette action n'est pas disponible.",
  'User not found in this company': 'Cet utilisateur est introuvable dans cette organisation.',
  'The renewal campaign has already been answered by this account.':
    'Vous avez déjà répondu à la campagne de renouvellement.',
  'engagementEndDate must be an ISO date (YYYY-MM-DD)':
    "La date de fin d'engagement n'est pas valide.",
  'must be one of: yes, no': 'Veuillez répondre par oui ou par non.',
  'User with Firebase UID "$1" not found in database': 'Cet utilisateur est introuvable.',
  'User with firebase_uid "$1" already exists': 'Un compte existe déjà pour cet identifiant.',
  'User with email "$1" already exists in this company':
    "Un utilisateur avec l'e-mail « $1 » existe déjà.",
  'User with phone "$1" already exists in this company':
    'Un utilisateur avec le numéro « $1 » existe déjà.',
  'User $1 does not have the COORDINATOR role': "Cette personne n'a pas le rôle coordinateur.",
  'User profile not found for user $1': 'Le profil de cet utilisateur est introuvable.',
  'User $1 not found': 'Cet utilisateur est introuvable.',
  'Missing required CSV columns: $1': 'Des colonnes obligatoires manquent dans le fichier CSV.',
  'Onboarding record not found for user $1': "Le dossier d'inscription est introuvable.",
  'Onboarding step template $1 not found': "Cette étape d'inscription est introuvable.",
  'Onboarding record for user $1 has already been validated.':
    "Ce dossier d'inscription a déjà été validé.",
  'Cannot abandon an already validated dossier for user $1':
    'Impossible d’abandonner un dossier déjà validé.',
  'Step template "$1" not found for tutor présentiel': "Cette étape d'inscription est introuvable.",
  'Step template "$1" not found for $2': "Cette étape d'inscription est introuvable.",
  'Step template with key "$1" already exists for role "$2" in this company':
    'Une étape avec ce nom existe déjà pour ce rôle.',
  'Step progress not found for user $1, step "$2"': "Cette étape d'inscription est introuvable.",
  'Step "$1" is locked. Complete the welcome meeting first to unlock post-registration steps.':
    "Cette étape est verrouillée. Terminez d'abord la réunion d'accueil.",
  'This renewal form targets $1; this account is $2.':
    'Ce formulaire de renouvellement ne correspond pas à votre profil.',
  'This account is not eligible for the renewal campaign ($1).':
    "Ce compte n'est pas éligible à la campagne de renouvellement.",

  // tutors inperson
  'This endpoint is reserved for tutor présentiel. Use /onboarding/tutor-remote for distanciel.':
    GenericErrors.accessDenied,
  'A tutor présentiel user can only access their own onboarding record.':
    'Vous ne pouvez consulter que votre propre dossier.',
  'This tutor is outside your visibility perimeter.': "Ce tuteur n'est pas dans votre périmètre.",
  'Établissements souhaités only apply to tutor présentiel records.':
    'Les établissements souhaités concernent uniquement les tuteurs en présentiel.',
  'Affectation workflow only applies to tutor présentiel records.':
    "L'affectation concerne uniquement les tuteurs en présentiel.",
  'Step "contract" is locked. Complete basic and personal information first.':
    "L'étape contrat est verrouillée. Complétez d'abord les informations de base et personnelles.",
  'An establishment must be selected (professionalSituation.student.higherEducationEstablishment[0].establishmentId) when professional situation is "student".':
    'Veuillez sélectionner un établissement.',
  'This action is restricted to tutors': 'Cette action est réservée aux tuteurs.',
  'This action requires coordinator, admin, or manager role':
    'Cette action est réservée aux coordinateurs, administrateurs et managers.',
  'Target user is not a tutor': "Cette personne n'est pas un tuteur.",
  'professionalSituation is required for tutors group':
    'La situation professionnelle est obligatoire pour les tuteurs.',
  'professionalSituation applies only to users in the tutors group':
    'La situation professionnelle concerne uniquement les tuteurs.',
  'availabilityStartTime must be earlier than availabilityEndTime':
    "L'heure de début doit être antérieure à l'heure de fin.",
  'availabilityDayOfWeek, availabilityStartTime and availabilityEndTime are required together':
    'Le jour et les horaires de disponibilité doivent être renseignés ensemble.',
  'establishmentId is required when sameEstablishment=true':
    'Veuillez sélectionner un établissement.',
  commitment_start_date_cannot_be_in_the_past: 'La date de début ne peut pas être dans le passé.',
  commitment_end_date_too_early: 'La date de fin doit être au moins 3 mois après la date de début.',
  'Affectation is not awaiting coordinator validation (status: $1).':
    "Cette affectation n'attend pas la validation du coordinateur.",
  'Affectation cannot be refused (status: $1).': 'Cette affectation ne peut pas être refusée.',
  'Affectation $1 not found': 'Cette affectation est introuvable.',
  'Cannot switch tutor track after onboarding has started.':
    'Vous ne pouvez plus changer de parcours tuteur.',
  'Cannot switch tutor track: onboarding record roleCode is "$1".':
    'Vous ne pouvez plus changer de parcours tuteur.',
  'Welcome meeting reservation applies only to $1 records.':
    "Cette réservation n'est pas disponible pour ce dossier.",
  'Welcome meeting step not found for user $1': "L'étape de réunion d'accueil est introuvable.",
  'RI participation can only be marked for a $1 record (got "$2").':
    "Cette action n'est pas disponible pour ce type de dossier.",
  'Welcome meeting participation can only be marked for a $1 record (got "$2").':
    "Cette action n'est pas disponible pour ce type de dossier.",
  'Welcome guide acknowledgement applies only to $1 (got "$2").':
    "Cette action n'est pas disponible pour ce type de dossier.",
  'Maximum $1 établissements souhaités.': 'Vous pouvez indiquer au maximum $1 établissements.',
  'The information meeting participation date is required.':
    "La date de participation à la réunion d'information est obligatoire.",
  'The information meeting participation date is invalid.':
    "La date de participation à la réunion d'information est invalide.",
  'The information meeting participation date must be between January 1 of the current year and today.':
    "La date de participation à la réunion d'information doit être comprise entre le 1er janvier de l'année en cours et aujourd'hui.",

  // tutors remote
  'Valued associative engagement is required when a professional situation is selected.':
    "L'engagement associatif valorisé est obligatoire.",
  'This endpoint is reserved for tutor distanciel (tutor_remote). Use /onboarding/tutor-presentiel for présentiel.':
    GenericErrors.accessDenied,
  'A tutor_remote user can only access their own onboarding record.':
    'Vous ne pouvez consulter que votre propre dossier.',
  'A tutor_remote user can only update their own onboarding record.':
    'Vous ne pouvez modifier que votre propre dossier.',
  'A tutor_remote user can only abandon their own dossier.':
    'Vous ne pouvez abandonner que votre propre dossier.',
  'Cannot archive tutor: active binome':
    'Impossible d’archiver ce tuteur : il est en cours de tutorat. Veuillez dissocier ses binômes actifs avant de procéder à l’archivage.',

  // students inperson
  'Status history is only available for student accounts':
    "L'historique des statuts est réservé aux comptes élèves.",
  'Invalid status for student account (registered, accompanied, deactivated).':
    "Ce statut n'est pas disponible pour un compte élève.",
  'CSV file is required': 'Veuillez sélectionner un fichier CSV.',
  'CSV file is empty': 'Le fichier CSV est vide.',
  'CSV file is required (multipart field: file, csv, or csvFile)':
    'Veuillez sélectionner un fichier CSV.',
  MISSING_REQUIRED_FIELD: 'Prénom ou nom manquant.',
  NAME_TOO_SHORT: 'Le prénom et le nom doivent contenir au moins 2 caractères.',
  INVALID_EMAIL: 'Email invalide.',
  MISSING_GENDER: 'Genre manquant.',
  INVALID_GENDER: 'Genre invalide (Garçon, Fille ou Non binaire).',
  MISSING_ESTABLISHMENT: 'Établissement manquant.',
  ESTABLISHMENT_CODE_NOT_FOUND:
    'Établissement introuvable, non validé ou hors de votre périmètre : choisissez-le dans la liste du modèle.',
  AMBIGUOUS_ESTABLISHMENT:
    'Plusieurs établissements correspondent : choisissez-le dans la liste du modèle.',
  ESTABLISHMENT_NOT_ALLOWED: 'Établissement non validé ou hors de votre périmètre.',
  MISSING_LEVEL: 'Niveau manquant.',
  LEVEL_NOT_FOUND: 'Niveau introuvable : choisissez-le dans la liste du modèle.',
  LEVEL_ESTABLISHMENT_MISMATCH: "Le niveau ne correspond pas au cycle de l'établissement.",
  DISPLAY_NAME_ALREADY_EXISTS:
    "Un élève avec le même prénom et les mêmes 2 premières lettres de nom existe déjà. S'il s'agit d'un autre élève, renseignez la colonne « Nom affiché » (ex. « Lina Mar »). Sinon, il est déjà importé.",
  ROLE_NOT_FOUND: InternalErrors.studentRoleNotFound,
  INVALID_UUID_PROFILE_ID: 'Identifiant de profil invalide.',
  UNKNOWN_IMPORT_ERROR: 'Cette ligne n’a pas pu être importée.',

  // students remote
  'Welcome meeting dev participation is not available in production.': GenericErrors.unavailable,
  'No welcome meeting reservation found for this student.':
    "Aucune réservation de réunion d'accueil pour cet élève.",
  'Cannot cancel a welcome meeting that has already been attended.':
    "Impossible d'annuler une réunion d'accueil déjà suivie.",
  'Only welcome_meeting sessions can be reserved through this endpoint.':
    "Seules les réunions d'accueil peuvent être réservées ici.",
  'Only planned welcome meetings can be reserved.':
    "Seules les réunions d'accueil planifiées peuvent être réservées.",
  'This student is not linked to a parent_remote account.':
    "Cet élève n'est pas lié à un compte parent.",
  'This student is not linked to a parent_remote account. Cannot reserve a welcome meeting.':
    "Cet élève n'est pas lié à un compte parent. Impossible de réserver une réunion d'accueil.",
  'This welcome meeting has already started and can no longer be reserved.':
    "Cette réunion d'accueil a déjà commencé et ne peut plus être réservée.",
  'Cannot archive student: active binome':
    'Impossible d’archiver cet élève : il est en cours de tutorat. Veuillez dissocier son binôme avant de procéder à l’archivage.',
  'Cannot reactivate student: no parent linked':
    'Impossible de réactiver cet élève : aucun parent n’est associé à son dossier. Veuillez lui associer un parent actif.',
  'Cannot reactivate student: parent is not active':
    'Impossible de réactiver cet élève : son parent doit être actif. Veuillez vérifier et finaliser le dossier du parent.',
  'Cannot reactivate student: parent is archived or suspended':
    'Impossible de réactiver cet élève tant que son parent archivé ou suspendu n’a pas été réactivé via le parcours dédié.',

  // parents
  'Onboarding record not found for parent user $1':
    "Le dossier d'inscription de ce parent est introuvable.",
  'Onboarding record not found for parent $1':
    "Le dossier d'inscription de ce parent est introuvable.",
  'Onboarding record not found for child $1':
    "Le dossier d'inscription de cet enfant est introuvable.",
  'Parent onboarding record not found for linkedUserId $1':
    "Le dossier d'inscription du parent est introuvable.",
  'These children are not linked to this parent account: $1':
    'Ces enfants ne sont pas liés à ce compte parent.',
  'No access code found in basic_information for this parent.':
    "Aucun code d'accès n'a été trouvé pour ce parent.",
  'No email address found for this parent.': 'Aucune adresse e-mail trouvée pour ce parent.',
  'This child has no linked parent.': "Cet enfant n'a pas de parent lié.",
  'No access code found for this child.': "Aucun code d'accès n'a été trouvé pour cet enfant.",
  'No email address found for the parent.': 'Aucune adresse e-mail trouvée pour le parent.',
  'A parent_remote user can only access their own onboarding record.':
    'Vous ne pouvez consulter que votre propre dossier.',
  'A parent_remote user can only access onboarding records of their own linked students.':
    'Vous ne pouvez consulter que les dossiers de vos enfants.',
  'A parent_remote user can only list their own children.':
    'Vous ne pouvez consulter que vos propres enfants.',
  'A parent_remote user can only invite children under their own account.':
    'Vous ne pouvez inviter que vos propres enfants.',
  'The same child was submitted more than once.':
    'Le même enfant a été sélectionné plusieurs fois.',
  'Cannot archive parent: one or more children have an active binome':
    'Impossible d’archiver ce parent : un ou plusieurs de ses enfants sont en cours de tutorat. Veuillez dissocier les binômes concernés avant de procéder à l’archivage.',

  // admins
  'You do not have access to this admin': "Vous n'avez pas accès à cet administrateur.",
  'Admin $1 not found': 'Cet administrateur est introuvable.',
  'Superadmin $1 not found': 'Ce super-administrateur est introuvable.',

  // volunteers
  'Volunteer $1 not found': 'Ce volontaire est introuvable.',

  // history
  'Utilisateur introuvable': 'Cet utilisateur est introuvable.',
  'Historique introuvable pour cet utilisateur':
    "L'historique est introuvable pour cet utilisateur.",

  // sessions inperson
  'You do not have access to this session': "Vous n'avez pas accès à cette séance.",
  'vacationZoneId does not match a vacation zone for this company':
    'Cette zone de vacances est introuvable.',
  'Cannot provide both activityId and newActivity/newActivityType':
    'Choisissez une activité existante ou créez-en une nouvelle, pas les deux.',
  'newActivityType requires newActivity to be provided':
    'Veuillez renseigner le nom de la nouvelle activité.',
  'recurrenceUpdateScope "following" cannot be combined with status changes; cancel or update occurrences separately.':
    'Vous ne pouvez pas modifier le statut en même temps que les séances suivantes. Annulez ou modifiez-les séparément.',
  'reasonDetail is required when reasonCode is OTHER': 'Veuillez préciser le motif.',
  'User already participant in this session': 'Cette personne participe déjà à la séance.',
  'Only excel export format is currently supported':
    "Seul l'export Excel est disponible pour le moment.",
  'Only cancellation (PLANNED → CANCELLED) is allowed via PATCH':
    'Seule l’annulation de la séance est possible ici.',
  'Room is already booked for another session at this date/time':
    'Cette salle est déjà réservée à cette date et à cette heure.',
  'startTime must be HH:mm': "L'heure de début n'est pas valide.",
  'endTime must be HH:mm': "L'heure de fin n'est pas valide.",
  'startTime must be in HH:mm format': "L'heure de début n'est pas valide.",
  'endTime must be in HH:mm format': "L'heure de fin n'est pas valide.",
  'Session report $1 not found': 'Ce compte rendu est introuvable.',
  'Session configuration $1 not found': 'Cette configuration de séance est introuvable.',
  'SessionGroup $1 not found or has no child sessions':
    'Ce groupe de séances est introuvable ou vide.',
  'SessionGroup $1 not found': 'Ce groupe de séances est introuvable.',
  'Session bulk job $1 not found': 'Cette tâche est introuvable.',
  'Session $1 not found': 'Cette séance est introuvable.',
  'Session $1 has no type': "Cette séance n'a pas de type.",
  'No recurring sessions found for group $1': 'Aucune séance récurrente trouvée pour ce groupe.',
  'Unregistered participant with ID $1 not found': 'Ce participant est introuvable.',
  'Participant with ID $1 not found': 'Ce participant est introuvable.',
  'Participant $1 not found': 'Ce participant est introuvable.',
  'Transition $1 → $2 with trigger "$3" is not allowed':
    "Ce changement de statut n'est pas autorisé.",
  'Room already booked for: $1': 'Cette salle est déjà réservée.',
  'Room or tutor already booked for: $1': 'Cette salle ou ce tuteur est déjà réservé.',
  'A session configuration for category "$1" already exists for this company.':
    'Une configuration existe déjà pour cette catégorie.',
  'Cannot delete: $1 session(s) are linked to this configuration. Update or deactivate it instead.':
    'Impossible de supprimer : des séances sont encore liées à cette configuration.',
  'Guest tokens are only available for information_meeting, welcome_meeting and information_meeting_in_person session types. This session is type: $1':
    'Les liens invités ne sont pas disponibles pour ce type de séance.',
  'Invalid $1: min minutes must be less than max minutes':
    'La durée minimale doit être inférieure à la durée maximale.',
  'participants is required for draft auto-assignment proposal':
    'Veuillez indiquer les participants pour proposer une répartition.',

  // sessions remote
  'You are not authorized to join this meeting. You must be an admin, the session manager, or a participant with coordinator/recruiter/volunteer role.':
    "Vous n'êtes pas autorisé à rejoindre cette réunion.",
  'You are not allowed to join this session': "Vous n'êtes pas autorisé à rejoindre cette séance.",
  'Guest token session ID does not match request': 'Le lien de cette réunion n’est plus valide.',
  'Session is not a remote session': "Cette séance n'est pas une séance à distance.",
  'Session is not joinable': "Cette séance n'est pas joignable.",
  'Session is not joinable yet': "Cette séance n'est pas encore joignable.",
  'No active LiveKit room for this session': 'La salle de visio n’est pas encore ouverte.',

  // tutorials
  'A video file is required': 'Ajoutez un fichier vidéo.',
  'Video file size must not exceed 50MB': 'La vidéo ne doit pas dépasser 50 Mo.',
  'Invalid video file type. Allowed: video/mp4, video/webm, video/quicktime':
    'Format vidéo non accepté. Utilisez MP4, WEBM ou MOV.',
  'Unknown role id': 'Un des rôles sélectionnés est introuvable.',
  'each value in roleIds must be a UUID': 'Un des rôles sélectionnés est invalide.',
  'Tutorial video $1 not found': 'Tutoriel introuvable.',
  'Help support must include every platform and mode':
    'Renseignez le lien et le téléphone pour l’administration et les usagers, en présentiel et en distanciel.',

  // visio backgrounds
  'Maximum of $1 visio backgrounds reached':
    'Le nombre maximum d’arrière-plans visio ($1) est atteint.',
  'Unknown visio background id in reorder list':
    'Un arrière-plan de la liste de réorganisation est introuvable.',
  'Visio background $1 not found': 'Arrière-plan visio introuvable.',

  // dashboard
  'Parent dashboard is only available to parent roles':
    'Le tableau de bord parent est réservé aux parents.',
  'Student dashboard is only available to student or parent roles':
    'Le tableau de bord élève est réservé aux élèves et aux parents.',
  'Student is not linked to this parent': "Cet élève n'est pas lié à ce parent.",
  'Students can only view their own dashboard':
    'Vous ne pouvez consulter que votre propre tableau de bord.',

  // help chat
  'At least one message is required': 'Veuillez saisir un message.',

  // reports inperson
  'Session report already exists for this session':
    'Un compte rendu existe déjà pour cette séance.',
  'Only staff can validate session reports': 'Seul le personnel peut valider les comptes rendus.',
  'Only submitted reports can be validated':
    'Seuls les comptes rendus soumis peuvent être validés.',
  'No audio file provided (field: audio)': 'Veuillez ajouter un fichier audio.',
  'No audio file provided': 'Veuillez ajouter un fichier audio.',
  'Duplicate idAuteur in authorFeedbacks': 'Un retour auteur est en double.',
  'You can only update your own session report feedback':
    'Vous ne pouvez modifier que vos propres retours.',
  'Duplicate participantUserId in studentFeedbacks': 'Un retour élève est en double.',
  'Only the report author can edit student feedbacks':
    'Seul l’auteur du compte rendu peut modifier les retours élèves.',
  'Unknown author feedback idAuteur: $1': 'Un retour auteur est invalide.',
  'studentFeedbacks must contain exactly $1 entries for this session':
    'Le nombre de retours élèves ne correspond pas à cette séance.',
  'Unknown studentFeedback participantUserId: $1': 'Un retour élève est invalide.',
  'Duplicate workedItemIds for student $1': 'Des éléments travaillés sont en double pour un élève.',
  'Unknown workedItemId "$1" for student $2': 'Un élément travaillé est invalide pour un élève.',
  'authorFeedbacks must contain exactly $1 entries for this session type':
    'Le nombre de retours auteur ne correspond pas à ce type de séance.',
  'Speech-to-text failed: $1': 'La transcription a échoué. Veuillez réessayer.',
  'CR generation failed: $1': 'La génération du compte rendu a échoué. Veuillez réessayer.',
  'Chat failed: $1': "L'assistant n'a pas pu répondre. Veuillez réessayer.",
  'Failed to save session report: $1':
    "L'enregistrement du compte rendu a échoué. Veuillez réessayer.",
  'Groq Whisper failed: $1': 'La transcription a échoué. Veuillez réessayer.',
  'Groq Chat failed: $1': "L'assistant n'a pas pu répondre. Veuillez réessayer.",

  // reports remote
  'You do not have access to this compte rendu': "Vous n'avez pas accès à ce compte rendu.",
  'You cannot edit the compte rendu view': 'Vous ne pouvez pas modifier ce compte rendu.',
  'Cannot edit a validated compte rendu': 'Impossible de modifier un compte rendu validé.',
  'Only a parent can submit scoring on behalf of a child':
    'Seul un parent peut évaluer pour un enfant.',
  'You do not have access to this student': "Vous n'avez pas accès à cet élève.",
  'Scoring entry does not belong to this student': "Cette évaluation n'appartient pas à cet élève.",
  'Student is not a participant on this session': 'Cet élève ne participe pas à cette séance.',
  'You cannot edit this scoring entry': 'Vous ne pouvez pas modifier cette évaluation.',
  'You cannot validate this compte rendu': 'Vous ne pouvez pas valider ce compte rendu.',
  'You cannot request correction on this compte rendu':
    'Vous ne pouvez pas demander une correction sur ce compte rendu.',
  'Invalid compte rendu fields': 'Certaines informations du compte rendu sont invalides.',
  'Invalid scoring fields': "Certaines informations de l'évaluation sont invalides.",
  'Scoring entry $1 not found': 'Cette évaluation est introuvable.',
  'Compte rendu $1 not found': 'Ce compte rendu est introuvable.',

  // vsc reports
  'Only coordinator, admin, or manager can validate VSC reports':
    'Seuls le coordinateur, l’administrateur ou le manager peuvent valider ces rapports.',
  'VSC report not found': 'Ce rapport est introuvable.',
  'You are not allowed to generate VSC daily reports':
    "Vous n'êtes pas autorisé à générer ces rapports.",
  'You do not have access to this report': "Vous n'avez pas accès à ce rapport.",
  'VSC daily report not found': 'Ce rapport quotidien est introuvable.',
  'You can only access your own VSC daily report':
    'Vous ne pouvez consulter que votre propre rapport quotidien.',
  'Report is validated and cannot be edited': 'Ce rapport est validé et ne peut plus être modifié.',
  'You can only edit your own VSC daily report':
    'Vous ne pouvez modifier que votre propre rapport quotidien.',
  'Report is not yet available (before visible_at)': "Ce rapport n'est pas encore disponible.",
  'Only the assigned VSC, an admin, a coordinator, or a manager can edit this daily report':
    'Vous ne pouvez pas modifier ce rapport quotidien.',
  'Rating is required to submit': 'Veuillez renseigner une note pour soumettre.',
  'Only admin, manager, or coordinator can validate a daily VSC report':
    'Vous ne pouvez pas valider ce rapport quotidien.',
  'You can only delete your own VSC daily report':
    'Vous ne pouvez supprimer que votre propre rapport quotidien.',
  'Insufficient permissions to delete this report': 'Vous ne pouvez pas supprimer ce rapport.',
  'VSC report $1 not found': 'Ce rapport est introuvable.',

  // establishments
  'You do not have access to this establishment': "Vous n'avez pas accès à cet établissement.",
  'Establishment not found': 'Cet établissement est introuvable.',
  'Establishment not found for this company': 'Cet établissement est introuvable.',
  'One or more establishments do not exist or do not belong to this company':
    "Un ou plusieurs établissements n'appartiennent pas à cette organisation.",
  'One or more establishments do not belong to this company.':
    "Un ou plusieurs établissements n'appartiennent pas à cette organisation.",
  'One or more coordinators do not exist or do not belong to this company':
    "Un ou plusieurs coordinateurs n'appartiennent pas à cette organisation.",
  'latitude must be a numeric string': "La latitude n'est pas valide.",
  'longitude must be a numeric string': "La longitude n'est pas valide.",
  'School class not found for this company': 'Cette classe est introuvable.',
  'School class must belong to an establishment assigned to this user':
    "Cette classe n'appartient pas aux établissements de l'utilisateur.",
  'A class with this name already exists for this establishment and school year':
    'Une classe portant ce nom existe déjà pour cet établissement et cette année scolaire.',
  'Establishment $1 not found or does not belong to this company':
    "Cet établissement est introuvable ou n'appartient pas à cette organisation.",
  'Establishment "$1" was not found for this company': 'L’établissement « $1 » est introuvable.',
  'Establishment $1 not found': 'Cet établissement est introuvable.',
  'Room $1 not found': 'Cette salle est introuvable.',
  'Contact function with ID $1 not found': 'Cette fonction de contact est introuvable.',
  'A function with label "$1" already exists': 'Une fonction avec le libellé « $1 » existe déjà.',
  'School class $1 not found': 'Cette classe est introuvable.',
  'Invalid $1: must be "true" or "false"': "Cette information n'est pas valide.",
  'Invalid $1: must be ISO date YYYY-MM-DD': "Cette date n'est pas valide.",
  'Invalid $1: must be a valid date': "Cette date n'est pas valide.",

  // partners
  'You do not have access to this partner': "Vous n'avez pas accès à ce partenaire.",
  'Partner $1 not found': 'Ce partenaire est introuvable.',

  // sport clubs
  'You do not have access to this sport club': "Vous n'avez pas accès à ce club sportif.",
  'Sport club $1 not found': 'Ce club sportif est introuvable.',

  // companies
  'Company mismatch': "L'organisation ne correspond pas.",
  'Company context required': "L'organisation est manquante.",
  'companyId is required': "L'organisation est manquante.",
  'companyId does not match the authenticated account.':
    'Ce compte ne correspond pas à l’organisation connectée.',
  'Company with code "$1" already exists': 'Une organisation avec le code « $1 » existe déjà.',
  'Company $1 not found': 'Cette organisation est introuvable.',

  // school years
  'School year not found for this company': 'Cette année scolaire est introuvable.',
  'No current school year found. Please set a school year as current or provide schoolYearId.':
    'Aucune année scolaire en cours. Définissez-en une comme année courante.',
  'No current school year found. Please set a school year as current.':
    'Aucune année scolaire en cours. Définissez-en une comme année courante.',
  'School year code "$1" already exists': 'Une année scolaire avec le code « $1 » existe déjà.',
  'School year $1 not found': 'Cette année scolaire est introuvable.',

  // levels
  'Level not found for this company': 'Ce niveau est introuvable.',
  'One or more levels do not belong to this company.':
    "Un ou plusieurs niveaux n'appartiennent pas à cette organisation.",
  'Level code "$1" already exists': 'Un niveau avec le code « $1 » existe déjà.',
  'Level $1 not found': 'Ce niveau est introuvable.',

  // subjects
  'You do not have access to this subject': "Vous n'avez pas accès à cette matière.",
  'Subject code "$1" already exists': 'Une matière avec le code « $1 » existe déjà.',
  'Subject $1 not found': 'Cette matière est introuvable.',

  // programs
  'You do not have access to this program': "Vous n'avez pas accès à ce programme.",
  'No file uploaded.': 'Veuillez sélectionner un fichier.',
  'Invalid Excel file.': "Ce fichier Excel n'est pas valide.",
  'Excel file has no sheets.': 'Ce fichier Excel est vide.',
  'startTime must be HH:MM or HH:MM:SS': "L'heure de début n'est pas valide.",
  'endTime must be HH:MM or HH:MM:SS': "L'heure de fin n'est pas valide.",
  'Program code "$1" already exists': 'Un programme avec le code « $1 » existe déjà.',
  'Program $1 not found': 'Ce programme est introuvable.',

  // specialties
  'One or more specialty IDs are invalid': 'Une ou plusieurs spécialités sont invalides.',
  'Specialties can only be set for users with the student role':
    'Les spécialités concernent uniquement les élèves.',
  'Specialties can only be set for students': 'Les spécialités concernent uniquement les élèves.',
  'User profile is required to set specialties':
    'Le profil est requis pour enregistrer les spécialités.',
  'Specialty code "$1" already exists': 'Une spécialité avec le code « $1 » existe déjà.',
  'Specialty $1 not found': 'Cette spécialité est introuvable.',

  // holidays
  'Vacation zone not found for this company': 'Cette zone de vacances est introuvable.',
  'You do not have access to this holiday': "Vous n'avez pas accès à ces vacances.",
  'Holiday $1 not found': 'Ces vacances sont introuvables.',

  // calendar
  'startDate and endDate are required': 'Veuillez renseigner les dates de début et de fin.',
  'Invalid date format': "Cette date n'est pas valide.",
  'startDate must be before endDate': 'La date de début doit être antérieure à la date de fin.',

  // assessments
  'Accès refusé à ce bilan.': 'Accès refusé à ce bilan.',
  'Bilan introuvable.': 'Bilan introuvable.',
  "Vous n'êtes pas autorisé à créer ce type de bilan.":
    "Vous n'êtes pas autorisé à créer ce type de bilan.",
  'Un établissement est requis pour ce type de bilan.':
    'Un établissement est requis pour ce type de bilan.',
  'Établissement introuvable.': 'Établissement introuvable.',
  'Un coordinateur chargé de remplir ce bilan est requis.':
    'Un coordinateur chargé de remplir ce bilan est requis.',
  'Sélectionnez au moins un intervenant qui remplira le bilan.':
    'Sélectionnez au moins un intervenant qui remplira le bilan.',
  'Un VSC concerné est requis pour ce bilan.': 'Un VSC concerné est requis pour ce bilan.',
  'Un bilan de ce type existe déjà pour ce VSC sur cet établissement.':
    'Un bilan de ce type existe déjà pour ce VSC sur cet établissement.',
  'Le coordinateur désigné est introuvable ou n’a pas ce rôle.':
    'Le coordinateur désigné est introuvable ou n’a pas ce rôle.',
  "Vous n'êtes pas autorisé à modifier ce bilan.": "Vous n'êtes pas autorisé à modifier ce bilan.",
  "Vous n'êtes pas autorisé à soumettre ce bilan.":
    "Vous n'êtes pas autorisé à soumettre ce bilan.",
  'Ce bilan est déjà validé et ne peut plus être soumis.':
    'Ce bilan est déjà validé et ne peut plus être soumis.',
  "Vous n'êtes pas autorisé à valider ce bilan.": "Vous n'êtes pas autorisé à valider ce bilan.",
  'Seul un bilan soumis peut être validé.': 'Seul un bilan soumis peut être validé.',
  "Vous n'êtes pas autorisé à supprimer ce bilan.":
    "Vous n'êtes pas autorisé à supprimer ce bilan.",
  'Le contenu du bilan est requis avant soumission.':
    'Le contenu du bilan est requis avant soumission.',
  'Merci de renseigner au moins une évaluation dans la grille.':
    'Merci de renseigner au moins une évaluation dans la grille.',
  'La date de naissance du volontaire est requise avant soumission.':
    'La date de naissance du volontaire est requise avant soumission.',
  'L’appréciation globale est requise avant soumission.':
    'L’appréciation globale est requise avant soumission.',

  // pairs inperson
  'Student-tutor pair not found or access denied': 'Ce binôme est introuvable ou accès refusé.',

  // pairs remote
  'An archived binome cannot change status': 'Un binôme archivé ne peut pas changer de statut.',
  'Cannot update manager of an archived binome':
    "Impossible de modifier le responsable d'un binôme archivé.",
  'New manager not found or does not have coordinator/manager role':
    'Le nouveau responsable est introuvable ou n’a pas le bon rôle.',
  'This binome is already archived': 'Ce binôme est déjà archivé.',
  'Binôme $1 not found or access denied': 'Ce binôme est introuvable ou accès refusé.',
  'Binôme $1 not found': 'Ce binôme est introuvable.',

  // matching inperson
  'Chaque élève doit être assigné à un tuteur ou un volontaire pour les séances de type aide aux devoirs, atelier, tutorat ou bureau d’aide rapide.':
    'Chaque élève doit être assigné à un tuteur ou un volontaire pour les séances de type aide aux devoirs, atelier, tutorat ou bureau d’aide rapide.',
  'Tutorat individuel : un seul tuteur ou volontaire est autorisé par séance.':
    'Tutorat individuel : un seul tuteur ou volontaire est autorisé par séance.',
  "Vous n'êtes pas autorisé à ajouter ou retirer des élèves sur vos séances. Contactez votre coordinateur.":
    "Vous n'êtes pas autorisé à ajouter ou retirer des élèves sur vos séances. Contactez votre coordinateur.",
  'Impossible de modifier les participants : le compte rendu est validé.':
    'Impossible de modifier les participants : le compte rendu est validé.',
  'At least one tutor or volunteer is required': 'Au moins un tuteur ou un bénévole est requis.',
  'Session has no establishment': "Cette séance n'a pas d'établissement.",
  'This binôme cœur already exists': 'Ce binôme cœur existe déjà.',
  "Chaque élève ne peut être associé qu'à un seul tuteur pour ce type de séance ($1).":
    "Chaque élève ne peut être associé qu'à un seul tuteur pour ce type de séance.",
  'Tuteur ou élève déjà réservé sur ce créneau — $1':
    'Tuteur ou élève déjà réservé sur ce créneau.',
  'Animator already booked on this slot — $1': 'Un animateur est déjà réservé sur ce créneau.',
  'Binôme cœur $1 not found': 'Ce binôme cœur est introuvable.',

  // matching remote
  'Matching user $1 not found': 'Cet utilisateur est introuvable pour le matching.',
  'Availability user is required': 'L’utilisateur est requis pour vérifier les disponibilités.',
  'Availability slots are required': 'Veuillez indiquer au moins un créneau.',

  // events
  'Event end date must be on or after the start date':
    'La date de fin doit être identique ou postérieure à la date de début.',
  'One or more activity IDs are invalid': 'Une ou plusieurs activités sont invalides.',
  'An event must have at least one organizer (tutor, volunteer, or coordinator)':
    'Un événement doit avoir au moins un organisateur.',
  'Event with ID $1 not found': 'Cet événement est introuvable.',

  // event summaries
  'Event synthesis with ID $1 not found': 'Cette synthèse est introuvable.',

  // post its
  'Post-it not found': 'Ce post-it est introuvable.',
  'You do not have access to this post-it': "Vous n'avez pas accès à ce post-it.",
  'You do not have permission to edit this post-it':
    "Vous n'avez pas la permission de modifier ce post-it.",
  'Only the creator can archive this post-it': 'Seul le créateur peut archiver ce post-it.',
  'Archived post-it not found': 'Ce post-it archivé est introuvable.',
  'Only the creator can permanently delete this post-it':
    'Seul le créateur peut supprimer définitivement ce post-it.',
  'You do not have permission to archive this post-it':
    "Vous n'avez pas la permission d'archiver ce post-it.",
  'You do not have permission to change priority of this post-it':
    "Vous n'avez pas la permission de modifier la priorité de ce post-it.",
  'You do not have permission to change status of this post-it':
    "Vous n'avez pas la permission de modifier le statut de ce post-it.",
  'Only the creator or users with write permission can share this post-it':
    "Seul le créateur ou une personne avec droit d'écriture peut partager ce post-it.",
  'Only the creator or users with write permission can remove shares':
    "Seul le créateur ou une personne avec droit d'écriture peut retirer les partages.",
  'Share not found': 'Ce partage est introuvable.',
  'Either targetRoleCode or targetUserId must be provided':
    'Veuillez indiquer un rôle ou une personne.',
  'specificDateFrom is required when recurrenceType is specific_date':
    'Veuillez renseigner une date pour cette récurrence.',
  'specificDateFrom is required when recurrence is specific_date':
    'Veuillez renseigner une date pour cette récurrence.',
  'Template not found': 'Ce modèle est introuvable.',
  'Template not found or has no schedule config': "Ce modèle n'a pas de planification.",
  'You are not authorized to grant post-it config access to this role.':
    "Vous n'êtes pas autorisé à donner l'accès à la configuration des post-its pour ce rôle.",
  'You are not authorized to configure post-its for the specified role(s). Please check your hierarchy level.':
    "Vous n'êtes pas autorisé à configurer les post-its pour ce rôle.",
  'You are not authorized to configure post-its for the specified role. Please check your hierarchy level.':
    "Vous n'êtes pas autorisé à configurer les post-its pour ce rôle.",
  'Assigned post-it template $1 not found': 'Ce modèle de post-it est introuvable.',
  'Template $1 not found': 'Ce modèle est introuvable.',

  // roles
  'You do not have access to this role': "Vous n'avez pas accès à ce rôle.",
  'Role not found': 'Ce rôle est introuvable.',
  'System roles cannot be modified': 'Les rôles système ne peuvent pas être modifiés.',
  'System roles cannot be deleted': 'Les rôles système ne peuvent pas être supprimés.',
  'System role groups cannot be modified':
    'Les groupes de rôles système ne peuvent pas être modifiés.',
  'System role groups cannot be deleted':
    'Les groupes de rôles système ne peuvent pas être supprimés.',
  'Role group $1 not found': 'Ce groupe de rôles est introuvable.',
  "Role '$1' not found for company $2": 'Ce rôle est introuvable.',
  'Role $1 not found': 'Ce rôle est introuvable.',
  'Role code "$1" already exists': 'Un rôle avec le code « $1 » existe déjà.',
  'Role group code "$1" already exists': 'Un groupe de rôles avec le code « $1 » existe déjà.',
  'Source role $1 not found': 'Ce rôle est introuvable.',
  'Profile $1 not found': 'Ce profil est introuvable.',
  'Profile code "$1" already exists': 'Un profil avec le code « $1 » existe déjà.',
  'Resource $1 not found': 'Cette ressource est introuvable.',
  'Rule override $1 not found': 'Cette dérogation est introuvable.',
  'Rule $1 not found': 'Cette règle est introuvable.',

  // interface elements
  'Cannot clone interface bindings from the same role':
    'Impossible de copier les accès depuis le même rôle.',
  'System roles cannot have interface bindings modified':
    'Les accès des rôles système ne peuvent pas être modifiés.',
  'Provide either cloneInterfaceBindingsFromRoleId or interfaceBindings, not both':
    'Choisissez de copier les accès d’un rôle ou de les définir, pas les deux.',
  'One or more interface element IDs are invalid, belong to another company, or are deleted':
    'Un ou plusieurs éléments d’interface sont invalides.',

  // notifications
  'Notification not found': 'Cette notification est introuvable.',

  // gamification
  'Niveau not found': 'Ce niveau est introuvable.',
  'Badge not found': 'Ce badge est introuvable.',
  'XP rule not found': 'Cette règle d’XP est introuvable.',

  // activities
  'You do not have access to this activity': "Vous n'avez pas accès à cette activité.",
  'Activity with code "$1" already exists for this company':
    'Une activité avec le code « $1 » existe déjà.',
  'Activity $1 not found': 'Cette activité est introuvable.',

  // activity types
  'You do not have access to this activity type': "Vous n'avez pas accès à ce type d'activité.",
  'Activity type with code "$1" already exists for this company':
    "Un type d'activité avec le code « $1 » existe déjà.",
  'Activity type $1 not found': "Ce type d'activité est introuvable.",

  // enterprises
  'Employer "$1" was not found': 'L’entreprise « $1 » est introuvable.',
  'An employer must be selected $1 when professional situation is "$2".':
    'Veuillez sélectionner une entreprise.',

  // branding
  'Theme $1 not found': 'Ce thème est introuvable.',
  'Theme code "$1" already exists': 'Un thème avec le code « $1 » existe déjà.',
  'Media asset $1 not found': 'Ce média est introuvable.',
  'Email template code "$1" already exists': 'Un modèle d’e-mail avec ce code existe déjà.',
  'Email template $1 not found': 'Ce modèle d’e-mail est introuvable.',
  'Document template $1 not found': 'Ce modèle de document est introuvable.',
  'Company with code "$1" not found': 'Cette organisation est introuvable.',
  'Company with id "$1" not found': 'Cette organisation est introuvable.',

  // preferences
  'User context is required': 'Le contexte utilisateur est manquant.',
  'No preference fields provided': 'Aucune préférence à enregistrer.',
  'Interface name is required': "Le nom de l'interface est obligatoire.",

  // search
  'Search failed': 'La recherche a échoué. Veuillez réessayer.',

  // geography
  'No departments found for country $1': 'Aucun département trouvé pour ce pays.',
  'No partners found': 'Aucun partenaire trouvé.',
  'Catalog key is required': 'La clé de catalogue est obligatoire.',
  'No options found for catalog $1': 'Aucune option trouvée pour ce catalogue.',

  // files
  'No file provided': 'Veuillez sélectionner un fichier.',
  'File not found in storage': 'Le fichier est introuvable.',
  'File size must not exceed $1MB': 'Le fichier ne doit pas dépasser $1 Mo.',
  'File size must not exceed 10MB': 'Le fichier ne doit pas dépasser 10 Mo.',
  'Invalid file type. Allowed: $1': 'Ce type de fichier n’est pas accepté.',
  'Invalid file type. Only PDF is allowed': 'Seul un fichier PDF est accepté.',

  // integrations
  'Please provide a class': 'Veuillez indiquer une classe.',
  'Unknown Maxicours class': 'Cette classe Maxicours est inconnue.',
  'Please provide a valid token': 'Ce lien n’est pas valide.',
  'Invalid Maxicours token': 'Ce lien Maxicours n’est pas valide.',
  'MAXICOURS_SECRET / MAXICOURS_URL is not configured': GenericErrors.unavailable,
  'class must be one of: $1': "Cette classe n'est pas reconnue.",

  // calendar
  'Calendar sync is not configured': InternalErrors.calendarNotConfigured,
  'Calendar OAuth state is invalid': InternalErrors.calendarOauthDenied,
  'Calendar connection not found': InternalErrors.calendarReconnectRequired,
  'Calendar sync prefs not found': InternalErrors.calendarReconnectRequired,
  'Calendar sync job not found': InternalErrors.calendarSyncFailed,
  'Google Calendar is not configured': InternalErrors.calendarNotConfigured,
  'Outlook Calendar is not configured': InternalErrors.calendarNotConfigured,
  'Failed to connect calendar provider': InternalErrors.calendarOauthDenied,
  'Calendar token payload is corrupt': InternalErrors.calendarSyncFailed,
  'Calendar OAuth token was revoked': InternalErrors.calendarTokenRevoked,
  'Another calendar provider is already connected': InternalErrors.calendarOtherProviderConnected,
  'This email domain cannot connect to this calendar provider':
    InternalErrors.calendarProviderEmailMismatch,
  'prefs.enabledTypes should not be empty': InternalErrors.calendarTypesRequired,

  // shared
  'You do not have access to this $1': "Vous n'avez pas accès à $1.",
  'This action requires one of the following roles: $1': GenericErrors.accessDenied,
  'Named account $1 not found': '$1 introuvable.',
  'nested property $1 must be either object or array': 'Cette information n’est pas au bon format.',
  'property $1 should not exist': 'Une information inattendue a été envoyée.',
};
